import React, { useState, useEffect } from 'react';

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Componentes 

// Funciones para obtener datos
import ObtenerPais from '../functions/ObtenerPais';
import GuardarProvincia from '../functions/GuardarProvincia';
import ObtenerProvincia from '../functions/ObtenerProvincia';
import InsertarProvincia from '../functions/InsertarProvincia';

// Tipos de datos
import PaisEditar from '../types/PaisEditar';
import ProvinciaTabla from '../types/ProvinciaTabla';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarProvinciaProps {
    idPais: number;
    idProvincia: number;
    guardarCambios: (provincia: ProvinciaTabla) => void;
    cancelarCambios: () => void;
    eliminando: boolean;
}

export const EditarProvincia: React.FC<EditarProvinciaProps> = ({ idPais, idProvincia, guardarCambios, cancelarCambios, eliminando = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [pais, setPais] = useState<PaisEditar | null>(null);
    const [provincia, setProvincia] = useState<ProvinciaTabla | null>(null);

    const [errorPais, setErrorPais] = useState<string | null>(null);
    const [errorProvincia, setErrorProvincia] = useState<string | null>(null);

    const [localProvincia, setLocalProvincia] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localIdPais, setLocalIdPais] = useState(0);


    useEffect(() => {
        
        const fetchDatos = async () => {
            try {
                const dataPais = await ObtenerPais(idPais);
                if (dataPais.pais_ok === 'si') {
                    // Todo ok, pais recuperado
                    const data = await ObtenerProvincia(idProvincia);
                    if (data.provincia_ok === 'si') {

                        // Todo ok, provincia recuperada
                        const provinciaRecuperada: ProvinciaTabla = {
                            id: data.provincia[0].id,
                            provincia: data.provincia[0].provincia,
                            habilitado: data.provincia[0].habilitado,
                            fch_crea: data.provincia[0].fecha_creacion,
                            fch_modi: data.provincia[0].fecha_modificacion,
                            dado_baja: data.provincia[0].dado_de_baja,
                            fch_baja: data.provincia[0].fecha_baja,
                            pais_id: dataPais.pais[0].pais_id,
                            pais_nombre: dataPais.pais[0].pais_nombre
                        };

                        setProvincia(provinciaRecuperada);

                        setLocalProvincia(data.provincia[0].provincia);
                        setLocalHabilitado(data.provincia[0].habilitado);
                        setLocalDadoBaja(data.provincia[0].dado_de_baja);
                        setLocalFchCrea(data.provincia[0].fecha_creacion);
                        setLocalFchModi(data.provincia[0].fecha_modificacion);
                        if (data.provincia[0].dado_de_baja === 'N') {
                            setLocalFchBaja('');
                        } else {
                            setLocalFchBaja(data.provincia[0].fecha_baja);
                        }
                        setLocalIdPais(dataPais.pais[0].id);
                        setErrorPais(null);

                    } else {
                        
                        // Hubo errores al recuperar el pais
                        setErrorPais(data.mensaje);
                        setProvincia(null);
                        setLocalProvincia('');
                        setLocalFchCrea('');
                        setLocalFchModi('');
                        setLocalFchBaja('');
                        setLocalHabilitado('');
                        setLocalDadoBaja('');
                        setLocalIdPais(0);
                    }
                } else {
                    setErrorPais(dataPais.mensaje);
                }
                setIsLoading(false);
            } catch (err) {
                setErrorPais('Error al obtener la información de la base de datos.');
                setIsLoading(false);
                setProvincia(null);
                setLocalProvincia('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalFchBaja('');
                setLocalHabilitado('');
                setLocalDadoBaja('');
                setLocalIdPais(0);
            }
        };

        if (idProvincia > 0) {
            // Estoy editando una provincia
            fetchDatos();

        } else {
            // Estoy creando una nueva provincia
            setIsLoading(false);
            setProvincia({id: 0, provincia: '', habilitado: 'S', fch_crea: '', fch_modi: '', dado_baja: 'N', fch_baja: '', pais_id: idPais, pais_nombre: ''});
            
            setLocalHabilitado('S');
            setLocalDadoBaja('N');
        }

    }, []);

    const handleChangeLocalHabitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    const guardarCambiosLocal = async () => {

        if (idProvincia === 0) {
            // Crear nueva provincia

            const ProvinciaNueva: ProvinciaTabla = {
                id: 0,
                provincia: localProvincia,
                habilitado: localHabilitado,
                fch_crea: '',
                fch_modi: '',
                dado_baja: localDadoBaja,
                fch_baja: '',
                pais_id: idPais,
                pais_nombre: provincia?.pais_nombre || ''
            };

            const respuesta = await InsertarProvincia(ProvinciaNueva);
            if (respuesta.provincia_guardada === 'si') {
                guardarCambios(ProvinciaNueva);
            } else {
                alert('Error al guardar los cambios: ' + respuesta.mensaje);
            }
            
        } else {

            if (eliminando) {
                
                // Se realiza la baja lógica del pais
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);

                // Modificar datos pais
                const ProvinciaEditada: ProvinciaTabla = {
                    id: idProvincia,
                    provincia: localProvincia,
                    habilitado: 'N',
                    fch_crea: localFchCrea,
                    fch_modi: guardarFecha,
                    dado_baja: 'S',
                    fch_baja: guardarFecha,
                    pais_id: localIdPais,
                    pais_nombre: provincia?.pais_nombre || ''
                };

                const respuesta = await GuardarProvincia(ProvinciaEditada);

                if (respuesta.provincia_guardada === 'si') {
                    guardarCambios(ProvinciaEditada);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Modificar pais
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                // Modificar datos pais
                const ProvinciaEditada: ProvinciaTabla = {
                    id: idProvincia,
                    provincia: localProvincia,
                    habilitado: localHabilitado,
                    fch_crea: localFchCrea,
                    fch_modi: guardarFecha,
                    dado_baja: localDadoBaja,
                    fch_baja: localDadoBaja === 'S' ? guardarFecha : '',
                    pais_id: localIdPais,
                    pais_nombre: provincia?.pais_nombre || ''
                };

                const respuesta = await GuardarProvincia(ProvinciaEditada);

                if (respuesta.provincia_guardada === 'si') {
                    guardarCambios(ProvinciaEditada);
                } else {
                    alert('Error al guardar los cambios');
                }
            }
        }
    };

    const handleChangeLocalProvincia = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalProvincia(event.target.value);
    };

    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && provincia) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminando ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Provincia: {provincia?.provincia}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idPais > 0 ? "Editando Provincia: " + provincia?.provincia : "Nueva Provincia"}</h1>
                    )}     
                    <div className="border-t-2 border-b-2 border-vp-azul w-full flex flex-row pt-3 flex-wrap justify-between items-start">
                        <div className="flex flex-col justify-start items-center max-w-[320px] flex-grow w-full">
                            <TextField id="provinca" label="Provincia" variant="outlined" value={localProvincia} autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalProvincia}
                                disabled={eliminando}
                            />
                        </div>
                        {(provincia?.id || 0) > 0 && (
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Habilitado
                                    </InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalHabitado}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-dadobaja"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Baja
                                    </InputLabel>
                                <Select
                                    labelId="label-dadobaja"
                                    id="dadobaja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminando ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};